import {
  CasualTournamentEntity,
  Game,
  RoomFilter,
  RoomFilterModel,
  TournamentType,
} from '@fantaz/shared/data-access/models';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  CASUAL_TOURNAMENT_FEATURE_KEY,
  CasualTournamentsState,
  casualTournamentAdapter,
} from './casual-tournament.reducer';

// Lookup the 'CasualTournament' feature state managed by NgRx
export const getCasualTournamentState =
  createFeatureSelector<CasualTournamentsState>(CASUAL_TOURNAMENT_FEATURE_KEY);

const { selectAll, selectEntities } = casualTournamentAdapter.getSelectors();

export const getCasualTournamentLoaded = createSelector(
  getCasualTournamentState,
  (state: CasualTournamentsState) => state.loaded,
);

export const getCasualTournamentError = createSelector(
  getCasualTournamentState,
  (state: CasualTournamentsState) => state.error,
);

export const getAllCasualTournament = createSelector(
  getCasualTournamentState,
  (state: CasualTournamentsState): CasualTournamentEntity[] => selectAll(state),
);

export const getCasualTournamentEntities = createSelector(
  getCasualTournamentState,
  (state: CasualTournamentsState) => selectEntities(state),
);

const getCasualTournamentGamesRaw = createSelector(
  getAllCasualTournament,
  (casuals): Game[] => {
    return casuals.map((entity) => entity.game);
  },
);
export const getCasualTournamentGames = createSelector(
  getCasualTournamentGamesRaw,
  (games) => {
    return games.filter(
      (game, index, self) =>
        index === self.findIndex((t) => t.key === game.key),
    );
  },
);

export const getSelectedId = createSelector(
  getCasualTournamentState,
  (state: CasualTournamentsState) => state.selectedId,
);

export const getSelected = createSelector(
  getCasualTournamentEntities,
  getSelectedId,
  (entities, selectedId) => (selectedId ? entities[selectedId] : undefined),
);

/**
 *
 */
export const getFilterByGames = createSelector(
  getCasualTournamentState,
  (state: CasualTournamentsState): string[] => state.filterByGames || [],
);

export const getSearchTerm = createSelector(
  getCasualTournamentState,
  (state: CasualTournamentsState): string => state.searchTerm || '',
);

export const getRoomFilters = createSelector(
  getCasualTournamentState,
  (state: CasualTournamentsState): RoomFilterModel[] => state.filters || [],
);

export const getLastTab = createSelector(
  getCasualTournamentState,
  (state: CasualTournamentsState): TournamentType | undefined =>
    state.lastTab || undefined,
);

export const getLoadedCasualTournament = createSelector(
  getAllCasualTournament,
  getCasualTournamentLoaded,
  (
    casualTournaments: CasualTournamentEntity[],
    loaded: boolean,
  ): { loaded: boolean; tournamentList: CasualTournamentEntity[] } => {
    return {
      loaded: loaded,
      tournamentList: casualTournaments,
    };
  },
);

export const getFilteredCasualTournaments = createSelector(
  getAllCasualTournament,
  getFilterByGames,
  getSearchTerm,
  getRoomFilters,
  (
    tournaments: CasualTournamentEntity[],
    games: string[],
    searchTerms: string,
    filters: RoomFilterModel[],
  ) => {
    return tournaments
      .filter((tournament: CasualTournamentEntity) => {
        // filter by games
        if (games && games.length && games.length > 0) {
          return games.includes(tournament.game.key);
        }

        return true;
      })
      .filter((tournament: CasualTournamentEntity) => {
        if (searchTerms !== '') {
          // search in tournament name
          if (
            tournament &&
            tournament.name &&
            tournament.name.toLowerCase().search(searchTerms.toLowerCase()) > -1
          ) {
            return true;
          }

          // search in game name
          if (
            tournament &&
            tournament.game &&
            tournament.game.name &&
            tournament.game.name
              .toLowerCase()
              .search(searchTerms.toLowerCase()) > -1
          ) {
            return true;
          }

          if (typeof tournament.prizeDetails !== 'undefined') {
            if (
              tournament.prizeDetails.prizeDetails &&
              tournament.prizeDetails.prizeDetails
                .toLowerCase()
                .search(searchTerms.toLowerCase()) > -1
            ) {
              return true;
            }

            if (
              tournament.prizeDetails.prizeName &&
              tournament.prizeDetails.prizeName
                .toLowerCase()
                .search(searchTerms.toLowerCase()) > -1
            ) {
              return true;
            }

            if (
              tournament.prizeDetails.prizeValue
                .toString()
                .search(searchTerms.toLowerCase()) > -1
            ) {
              return true;
            }
          }

          return false;
        }

        return true;
      })
      .filter((tournament: CasualTournamentEntity) => {
        let match = false;

        filters.forEach((filters: RoomFilterModel) => {
          filters?.values?.forEach((value: RoomFilter) => {
            if (value.selected) {
              const min = value.value.start;
              const max = value.value.end;
              let valuesToCompare: number[] = [];

              if (filters.filter === 'prize') {
                // get all prizes
                valuesToCompare = tournament.eventSchedules.reduce(
                  (acc: number[], cur) => {
                    return acc.concat(
                      cur.prizeDetails
                        .filter((p) => p.rank === 1)
                        .map((p) => p.prizeValue),
                    );
                  },
                  [],
                );
              }

              if (filters.filter === 'entryAmount') {
                // get all entry amounts
                valuesToCompare = tournament.eventSchedules.reduce(
                  (acc: number[], cur) => {
                    return acc.concat(cur.entryAmount);
                  },
                  [],
                );
              }

              const obj = valuesToCompare.find((p) => {
                if (value.type === 'less' && p < min) {
                  return true;
                }

                if (value.type === 'more' && p >= min) {
                  return true;
                }

                if (value.type === 'exact' && p == min) {
                  return true;
                }

                return p >= min && p <= max;
              });

              if (obj) {
                match = true;
              }
            }
          });
        });

        return filters.length > 0 ? match : true;
      })
      .sort((a, b) => {
        // If first tournament is completed and second is not, then first is greater
        if (a.isComplete && !b.isComplete) {
          return 1;
        }
        // If second tournament is completed and first is not, then second is greater
        if (!a.isComplete && b.isComplete) {
          return -1;
        }
        // If both are equal stay the same
        return 0;
      });
  },
);

export const getTimesUserTournamentPlayedItems = createSelector(
  getCasualTournamentState,
  (state: CasualTournamentsState) => state.timesUserTournamentPlayed,
);
